import {
	CheckCircleIcon,
	ChevronRightIcon,
	EyeIcon,
	EyeSlashIcon,
	InformationCircleIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown, Loader2 } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'

import { yupResolver } from '@hookform/resolvers/yup'
import { format } from 'date-fns'
import { toast } from 'sonner'
import { useMediaQuery } from 'usehooks-ts'
import * as yup from 'yup'

import { EditIcon, RotateLeftIcon, TaskIcon, TaskSquareIcon } from '@/assets/icons'
import { Button } from '@/components/ui/button'
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PaginationComponent } from '@/components/ui/pagination'
import {
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { primaryIbanOptions } from '@/constants'
import { capitalizeFirstLetter, formatIban, normalizeIban } from '@/lib/helper'
import { cn } from '@/lib/utils'
import userService from '@/services/user-service'
import { InputMask } from '@react-input/mask'

interface UsersTableProps {
	onBookingView: (id: number) => void
}

export interface UserUpdateForm {
	first_name: string
	last_name: string
	email: string
	iban: string
	phone_number: string
	address: string
	country: string
	zip: string
	reference_code: string
}

export const UsersTable = ({ onBookingView }: UsersTableProps) => {
	const searchableColumns = [
		{ label: 'User ID', value: 'id' },
		{ label: 'Vorname', value: 'first_name' },
		{ label: 'Nachname', value: 'last_name' },
		{ label: 'Email', value: 'email' },
	]

	const isSmallScreen = useMediaQuery('(max-width: 64rem)')

	const [expandedRows, setExpandedRows] = useState<number[]>([])
	const [hasNewsOffer, setHasNewsOffer] = React.useState('')
	const [filterValue, setFilterValue] = useState('')
	const [page, setPage] = useState(1)
	const [selectedColumn, setSelectedColumn] = useState('id')
	const [rowsPerPage, setRowsPerPage] = useState(50)
	const [isPasswordVisible, setIsPasswordVisible] = useState(false)
	const [sortColumn, setSortColumn] = useState('')
	const [sortDirection, setSortDirection] = useState('')
	const [isUpdateDialogVisible, setIsUpdateDialogVisible] = useState<number | null>(null)
	const [isUserUpdateDialogVisible, setIsUserUpdateDialogVisible] = useState<number | null>(null)

	const schema = yup.object<{ password: string }>().shape({
		password: yup.string().required('Passwort ist erforderlich!'),
	})

	const form = useForm<{ password: string }>({
		resolver: yupResolver(schema as any),
		defaultValues: { password: '' },
		mode: 'all',
	})

	const userSchema = yup.object<UserUpdateForm>().shape({
		first_name: yup.string().required('Name ist erforderlich!'),
		last_name: yup.string().required('Name ist erforderlich!'),
		email: yup
			.string()
			.required('E-Mail ist erforderlich')
			.email('Geben Sie eine gültige E-Mail-Adresse ein'),
		country: yup.string().required('Ort ist erforderlich!'),
		zip: yup.string().required('Postleitzahl ist erforderlich!'),
		iban: yup
			.string()
			.required('IBAN ist erforderlich!')
			.matches(/^DE(\s?\d){20}$/, "IBAN muss mit 'DE' beginnen und genau 20 Zahlen enthalten"),
		address: yup.string().required('Adresse ist erforderlich!'),
		phone_number: yup.string().required('Telefonnummer ist erforderlich!'),
		reference_code: yup.string().notRequired(),
	})

	const userForm = useForm<UserUpdateForm>({
		resolver: yupResolver(userSchema as any),
		mode: 'all',
	})

	const { data, refetch, isLoading } = useQuery(
		'fetch-users',
		() =>
			userService.fetchUsersData({
				pageNumber: page.toString(),
				pageSize: rowsPerPage.toString(),
				filterColumn: selectedColumn,
				filterValue,
			}),
		{
			refetchOnWindowFocus: false,
			onError: () => {
				toast.error('Error fetching users')
			},
		},
	)

	const { mutate: togglePassword, isLoading: updatingPassword } = useMutation({
		mutationKey: 'toggle-password',
		mutationFn: (data: TogglePasswordPayload) => userService.togglePassword(data),
	})

	const { mutate: updateUser, isLoading: updatingUser } = useMutation({
		mutationKey: 'update-user',
		mutationFn: ({ id, data }: { id: User['id']; data: UserUpdateForm }) =>
			userService.updateSpecificUserProfile(id, data),
	})

	const { mutate: updateUserStatus, isLoading: updatingStatus } = useMutation({
		mutationKey: 'update-user-status',
		mutationFn: ({ id, status }: { id: User['id']; status: 0 | 1 }) =>
			userService.updateUserStatus(id, status),
	})

	const sortedData = useMemo(() => {
		if (!data) return []
		if (!sortColumn) return data.data

		return [...data.data].sort((a, b) => {
			const aValue = a[sortColumn as keyof User]
			const bValue = b[sortColumn as keyof User]

			if (aValue == null || bValue == null) return 0

			let aVal: any = aValue
			let bVal: any = bValue

			if (aVal instanceof Date) aVal = aVal.getTime()
			if (bVal instanceof Date) bVal = bVal.getTime()

			const aParsed = typeof aVal === 'string' ? Date.parse(aVal) : NaN
			const bParsed = typeof bVal === 'string' ? Date.parse(bVal) : NaN

			if (!isNaN(aParsed) && !isNaN(bParsed)) {
				aVal = aParsed
				bVal = bParsed
			}

			if (typeof aVal === 'number' && typeof bVal === 'number') {
				return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
			}

			if (typeof aVal === 'string' && typeof bVal === 'string') {
				return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
			}

			return 0
		})
	}, [data, sortColumn, sortDirection])

	const filteredUsers = sortedData?.filter(user =>
		hasNewsOffer === 'yes' ? user.news_offer : hasNewsOffer === 'no' ? !user.news_offer : true,
	)

	useEffect(() => {
		refetch()
	}, [rowsPerPage, page])

	const toggleRow = (id: number) => {
		setExpandedRows(prev =>
			prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id],
		)
	}

	const handleSort = (column: string) => {
		if (sortColumn === column) {
			setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setSortColumn(column)
			setSortDirection('asc')
		}
	}

	const handlePasswordRevert = (id: number) => {
		const payload: TogglePasswordPayload = { id, action: 'revert' }
		togglePassword(payload, {
			onSuccess: res => {
				if (res?.status === 200) {
					toast.success('Password reverted successfully!')
				}
			},
			onError: (err: any) => {
				toast.error(err?.response?.data?.message)
			},
		})
	}

	const handleUserStatusUpdate = (id: number, status: 0 | 1) => {
		updateUserStatus(
			{ id, status },
			{
				onSuccess: res => {
					if (res?.status === 200) {
						toast.success(
							status === 1
								? 'Benutzerkonto wurde aktiviert.'
								: 'Benutzerkonto wurde deaktiviert.',
						)
						refetch()
					}
				},
				onError: (err: any) => {
					toast.error(err?.response?.data?.message ?? 'Status konnte nicht aktualisiert werden')
				},
			},
		)
	}

	const onSubmit = (data: { password: string }) => {
		const payload: TogglePasswordPayload = {
			id: isUpdateDialogVisible as number,
			action: 'update',
			newPassword: data.password,
		}
		togglePassword(payload, {
			onSuccess: res => {
				if (res?.status === 200) {
					toast.success('Password updated successfully!')
					form.reset()
					setIsUpdateDialogVisible(null)
				}
			},
			onError: (err: any) => {
				toast.error(err?.response?.data?.message)
			},
		})
	}

	const onUserSubmit = (data: UserUpdateForm) => {
		updateUser(
			{
				data: {
					...data,
					country: capitalizeFirstLetter(data.country),
					iban: normalizeIban(data.iban),
				},
				id: isUserUpdateDialogVisible!,
			},
			{
				onSuccess: res => {
					if (res?.status === 200) {
						toast.success('Profil erfolgreich aktualisiert')
						userForm.reset()
						setIsUserUpdateDialogVisible(null)
						refetch()
					}
				},
				onError: (err: any) => {
					toast.error(err?.response?.data?.message)
				},
			},
		)
	}

	const columns: ColumnDef<User>[] = [
		{
			accessorKey: 'id',
			header: ({ column }) => <DataTableColumnHeader column={column} title="User ID" />,
		},
		{
			accessorKey: 'first_name',
			accessorFn: (row: User) =>
				`${capitalizeFirstLetter(row.first_name)} ${capitalizeFirstLetter(row.last_name)}`,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
		},
		{
			accessorKey: 'email',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
		},
		{
			accessorKey: 'news_offer',
			accessorFn: (row: User) => (row.news_offer ? 'Yes' : 'No'),
			header: ({ column }) => <DataTableColumnHeader column={column} title="Werbung" />,
		},
		{
			accessorKey: 'status',
			accessorFn: (row: User) => (Number(row.status) === 1 ? 'Aktiv' : 'Deaktiviert'),
			header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
			cell: ({ row }) => (
				<div
					className={cn(
						'w-fit rounded-[18px] p-1.5 px-3',
						Number(row.original.status) === 1
							? 'bg-[#61B9641A] text-[#61B964]'
							: 'bg-[#FFEFF2] text-[#DB5358]',
					)}>
					{row.getValue('status')}
				</div>
			),
		},
		{
			accessorKey: 'createdAt',
			header: ({ column }) => <DataTableColumnHeader column={column} title="Datum" />,
			cell: ({ row }) => <div>{format(new Date(row.getValue('createdAt')), 'dd-MM-yyyy')}</div>,
		},
		{
			id: 'action-users',
			enableSorting: false,
			header: ({ column }) => <DataTableColumnHeader column={column} title="Aktionen" />,
			cell: ({ row }) => (
				<div className="flex items-center justify-center gap-0.5">
					<Tooltip>
						<TooltipTrigger
							onClick={() => onBookingView(row.original.id)}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<TaskIcon className="shrink-0" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Buchungen</p>
						</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger
							onClick={() => setIsUpdateDialogVisible(row.original.id)}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<EditIcon className="shrink-0" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Aktualisieren</p>
						</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger
							onClick={() => {
								userForm.reset({
									first_name: row.original.first_name,
									last_name: row.original.last_name,
									email: row.original.email,
									phone_number: row.original.phone_number,
									iban: formatIban(row.original.iban),
									zip: row.original.zip,
									country: row.original.country,
									reference_code: row.original.reference_code,
									address: row.original.address,
								})
								setIsUserUpdateDialogVisible(row.original.id)
							}}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<EyeIcon className="shrink-0 size-4" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Sicht</p>
						</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger
							disabled={updatingStatus}
							onClick={() =>
								handleUserStatusUpdate(
									row.original.id,
									Number(row.original.status) === 1 ? 0 : 1,
								)
							}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							{Number(row.original.status) === 1 ? (
								<XCircleIcon className="shrink-0 size-4" />
							) : (
								<CheckCircleIcon className="shrink-0 size-4" />
							)}
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">
								{Number(row.original.status) === 1 ? 'Deaktivieren' : 'Aktivieren'}
							</p>
						</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger
							onClick={() => handlePasswordRevert(row.original.id)}
							className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-[33px] flex items-center justify-center rounded-full">
							<RotateLeftIcon className="shrink-0" />
						</TooltipTrigger>
						<TooltipContent side="bottom">
							<p className="text-xs font-medium">Zurücksetzen</p>
						</TooltipContent>
					</Tooltip>
				</div>
			),
		},
	]

	const MobileUsersTable = () => {
		return (
			<Table>
				<TableHeader>
					<TableRow className="bg-[#F7F8F880] border-b-0! py-[13px] px-4 rounded-lg">
						<TableHead
							align="center"
							onClick={() => handleSort('id')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								User ID
								{sortColumn === 'id' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('first_name')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Name
								{sortColumn === 'first_name' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
						<TableHead
							align="center"
							onClick={() => handleSort('createdAt')}
							className="cursor-pointer text-center text-muted-foreground text-[10px] font-medium">
							<div className="flex items-center gap-1 justify-center">
								Datum
								{sortColumn === 'createdAt' ? (
									sortDirection === 'desc' ? (
										<ArrowDown size={12} />
									) : (
										<ArrowUp size={12} />
									)
								) : (
									<ChevronsUpDown size={12} />
								)}
							</div>
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{filteredUsers?.map((user, index) => (
						<React.Fragment key={user.id.toString() + index.toString()}>
							<TableRow
								className={cn(
									'h-14',
									expandedRows.includes(user.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
								)}
								onClick={() => toggleRow(user.id)}>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(user.id),
									})}>
									<div className="flex items-center">
										<ChevronRightIcon
											className={cn(
												'size-3 shrink-0 -mt-0.5',
												expandedRows.includes(user.id)
													? 'rotate-90 text-secondary'
													: 'text-muted-foreground',
											)}
										/>
										{user.id}
									</div>
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-[10px]', {
										'text-muted-foreground': !expandedRows.includes(user.id),
									})}>
									{capitalizeFirstLetter(user.first_name)} {capitalizeFirstLetter(user.last_name)}
								</TableCell>
								<TableCell
									align="center"
									className={cn('text-xs', {
										'text-muted-foreground': !expandedRows.includes(user.id),
									})}>
									{format(new Date(user.createdAt), 'dd-MM-yyyy')}
								</TableCell>
							</TableRow>
							{expandedRows.includes(user.id) && (
								<TableRow
									className={cn(
										'mt-2',
										!expandedRows.includes(user.id) ? '!border-b-0' : 'border-b border-[#F4F4F4]',
									)}>
									<TableCell className="pt-0" colSpan={3}>
										<div className="px-3 flex flex-col gap-4">
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Email</h6>
												<p className="text-secondary text-xs font-medium">{user.email}</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Werbung</h6>
												<p className="text-secondary text-xs font-medium">
													{user.news_offer ? 'Yes' : 'No'}
												</p>
											</div>
											<div className="flex items-center justify-between">
												<h6 className="text-muted-foreground text-[10px] font-medium">Status</h6>
												<p
													className={cn(
														'text-xs font-medium rounded-[18px] p-1.5 px-3',
														Number(user.status) === 1
															? 'bg-[#61B9641A] text-[#61B964]'
															: 'bg-[#FFEFF2] text-[#DB5358]',
													)}>
													{Number(user.status) === 1 ? 'Aktiv' : 'Deaktiviert'}
												</p>
											</div>

											<div className="flex items-center justify-between">
												<div className="flex items-center gap-1">
													<InformationCircleIcon className="text-muted-foreground size-3" />
													<h6 className="text-muted-foreground text-[10px] font-medium">Aktion</h6>
												</div>
												<div className="flex items-center gap-1">
													<div
														onClick={() => onBookingView(user.id)}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<TaskIcon className="shrink-0 max-lg:size-3" />
													</div>
													<div
														onClick={() => setIsUpdateDialogVisible(user.id)}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<EditIcon className="shrink-0 max-lg:size-3" />
													</div>
													<div
														onClick={() => {
															setIsUserUpdateDialogVisible(user.id)
															userForm.reset({
																first_name: user.first_name,
																last_name: user.last_name,
																email: user.email,
																phone_number: user.phone_number,
																iban: formatIban(user.iban),
																zip: user.zip,
																country: user.country,
																reference_code: user.reference_code,
																address: user.address,
															})
														}}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<EyeIcon className="shrink-0 max-lg:size-3" />
													</div>
													<div
														onClick={() =>
															handleUserStatusUpdate(user.id, Number(user.status) === 1 ? 0 : 1)
														}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														{Number(user.status) === 1 ? (
															<XCircleIcon className="shrink-0 max-lg:size-3" />
														) : (
															<CheckCircleIcon className="shrink-0 max-lg:size-3" />
														)}
													</div>
													<div
														onClick={() => handlePasswordRevert(user.id)}
														className="border cursor-pointer border-[#0B224D33] hover:border-secondary size-6 flex items-center justify-center rounded-full">
														<RotateLeftIcon className="shrink-0 max-lg:size-3" />
													</div>
												</div>
											</div>
										</div>
									</TableCell>
								</TableRow>
							)}
						</React.Fragment>
					))}
				</TableBody>
			</Table>
		)
	}

	return (
		<div className="rounded-[20px] bg-white max-lg:p-4 max-lg:pr-3 lg:px-4 lg:py-6 flex flex-col">
			<ResponsiveModal
				open={!!isUpdateDialogVisible}
				onOpenChange={() => {
					form.reset()
					setIsUpdateDialogVisible(null)
				}}>
				<ResponsiveModalContent className="gap-0 lg:max-w-2xl">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Passwort aktualisieren
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Geben Sie das aktualisierte Passwort ein
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...form}>
						<form onSubmit={form.handleSubmit(onSubmit)}>
							<div className="mt-3 lg:mt-8">
								<FormField
									control={form.control}
									name="password"
									render={({ field }) => (
										<FormItem className="mt-8">
											<FormLabel className="text-muted-foreground">
												Password <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<div className="relative">
													<Input type={isPasswordVisible ? 'text' : 'password'} {...field} />
													{isPasswordVisible ? (
														<EyeIcon
															onClick={() => setIsPasswordVisible(false)}
															className="size-5 lg:size-6 absolute right-4 top-1/2 -translate-y-1/2 text-secondary cursor-pointer"
														/>
													) : (
														<EyeSlashIcon
															onClick={() => setIsPasswordVisible(true)}
															className="size-5 lg:size-6 absolute right-4 top-1/2 -translate-y-1/2 text-secondary cursor-pointer"
														/>
													)}
												</div>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<Button
									variant="secondary"
									className="mt-14 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
									{updatingPassword && <Loader2 className="animate-spin" />}
									{updatingPassword ? 'Bitte warten' : 'Speichern'}
								</Button>
							</div>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<ResponsiveModal
				open={!!isUserUpdateDialogVisible}
				onOpenChange={() => {
					userForm.reset()
					setIsUserUpdateDialogVisible(null)
				}}>
				<ResponsiveModalContent className="gap-0 lg:max-w-3xl">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Profil aktualisieren
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="sr-only">
							Profil aktualisieren
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<Form {...userForm}>
						<form onSubmit={userForm.handleSubmit(onUserSubmit)}>
							<div className="grid mt-5 lg:mt-8 grid-cols-1 lg:grid-cols-2 lg:gap-x-4 gap-y-5">
								<FormField
									control={userForm.control}
									name="first_name"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-muted-foreground">
												Vorname <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Vorname"
													{...field}
													value={capitalizeFirstLetter(field.value)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={userForm.control}
									name="last_name"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-muted-foreground">
												Nachname <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Nachname"
													{...field}
													value={capitalizeFirstLetter(field.value)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={userForm.control}
									name="email"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="text-muted-foreground">
												Email <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Email" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={userForm.control}
									name="phone_number"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Telefonnummer <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Telefonnummer" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={userForm.control}
									name="iban"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												IBAN <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<InputMask
													{...primaryIbanOptions}
													value={field.value ?? ''}
													onChange={e => field.onChange(e.target.value)}
													placeholder="IBAN"
													component={Input}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={userForm.control}
									name="zip"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Postleitzahl <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input placeholder="Postleitzahl" {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={userForm.control}
									name="country"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">
												Ort <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Ort"
													{...field}
													value={capitalizeFirstLetter(field.value)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={userForm.control}
									name="reference_code"
									render={({ field }) => (
										<FormItem className="w-full">
											<FormLabel className="text-muted-foreground">Referenzcode</FormLabel>
											<FormControl>
												<Input placeholder="Referenzcode" disabled {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={userForm.control}
									name="address"
									render={({ field }) => (
										<FormItem className="w-full lg:col-span-2">
											<FormLabel className="text-muted-foreground">
												Adresse <span className="text-destructive-foreground">*</span>
											</FormLabel>
											<FormControl>
												<Input
													placeholder="Adresse"
													{...field}
													value={capitalizeFirstLetter(field.value)}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<Button
								variant="secondary"
								className="mt-10 w-full lg:h-[61px] rounded-[12px] font-extrabold text-white">
								{updatingUser && <Loader2 className="animate-spin" />}
								{updatingUser ? 'Bitte warten' : 'Aktualisieren'}
							</Button>
						</form>
					</Form>
				</ResponsiveModalContent>
			</ResponsiveModal>
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<div className="lg:hidden rounded-xl border border-[#EEEFF0] size-10 flex items-center justify-center">
						<TaskSquareIcon className="text-secondary" />
					</div>
					<h1 className="text-muted-foreground lg:text-secondary text-xs lg:text-2xl font-medium lg:font-extrabold">
						Benutzer Liste
					</h1>
				</div>
			</div>
			<div className="flex max-lg:mt-5 mt-6 flex-col gap-3 lg:gap-4">
				<div className="grid grid-cols-2 items-center gap-[9px] lg:hidden">
					<Select value={hasNewsOffer} onValueChange={setHasNewsOffer}>
						<SelectTrigger className="w-full h-10 text-xs">
							<SelectValue placeholder="Hat ein Nachrichtenangebot?" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="yes">Ja</SelectItem>
							<SelectItem value="no">Nein</SelectItem>
						</SelectContent>
					</Select>
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-full h-10 text-xs">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="lg:hidden flex flex-col gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="h-10 text-xs w-full">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<div className="flex lg:hidden items-center gap-2">
						<Input
							value={filterValue}
							onChange={e => setFilterValue(e.target.value)}
							className="h-10 text-xs"
							placeholder="Bezeichnung eingeben"
						/>
						<Button
							onClick={() => refetch()}
							variant="secondary"
							className="w-[73px] py-3 text-xs font-medium bg-secondary rounded-lg">
							Suche
						</Button>
					</div>
				</div>
				<div className="flex max-lg:hidden items-center gap-3">
					<Select value={selectedColumn} onValueChange={setSelectedColumn}>
						<SelectTrigger className="flex-1">
							<SelectValue placeholder="Kategorie" />
						</SelectTrigger>
						<SelectContent>
							{searchableColumns.map(column => (
								<SelectItem key={column.value} value={column.value}>
									{column.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Input
						value={filterValue}
						onChange={e => setFilterValue(e.target.value)}
						className="flex-1"
						placeholder="Bezeichnung eingeben"
					/>
					<Button
						onClick={() => refetch()}
						variant="secondary"
						className="w-[90px] font-medium bg-secondary rounded-xl">
						Suche
					</Button>
				</div>
			</div>
			<div className="flex max-lg:hidden items-center justify-between mt-6">
				<div className="flex items-center gap-4">
					<Select
						value={rowsPerPage.toString()}
						onValueChange={value => setRowsPerPage(Number(value))}>
						<SelectTrigger className="w-[127px]">
							<SelectValue placeholder="zeigen 50" />
						</SelectTrigger>
						<SelectContent>
							{[50, 80, 100, 200, 500].map(pageSize => (
								<SelectItem key={pageSize} value={pageSize.toString()}>
									zeigen {pageSize}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-8">
					<PaginationComponent
						currentPage={page}
						totalPages={data?.pagination?.totalPages ?? 1}
						onPageChange={setPage}
					/>
					<Select value={hasNewsOffer} onValueChange={setHasNewsOffer}>
						<SelectTrigger className="w-[247px]">
							<SelectValue placeholder="Hat ein Nachrichtenangebot?" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="yes">Ja</SelectItem>
							<SelectItem value="no">Nein</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>
			<div className="mt-4">
				{isSmallScreen ? (
					<MobileUsersTable />
				) : (
					<DataTable columns={columns} data={filteredUsers ?? []} isLoading={isLoading} />
				)}
			</div>
			<div className="mt-6 lg:mt-16 flex justify-center">
				<PaginationComponent
					currentPage={page}
					totalPages={data?.pagination?.totalPages ?? 1}
					onPageChange={setPage}
				/>
			</div>
		</div>
	)
}
